import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Modal, Button, Alert} from 'react-bootstrap';
import {IconCamera, IconSwitchHorizontal, IconRefresh} from '@tabler/icons-react';
import {FileData} from '../utils/fileUtils';

interface PhotoCaptureProps {
    show: boolean;
    onHide: () => void;
    onCapture: (file: FileData) => void;
    /** Longer edge of the saved photo; keeps uploads small on phones. */
    maxSize?: number;
    quality?: number;
}

/**
 * Takes a photo with the device camera and hands it over in the same shape an
 * uploaded file has, so a file field can accept either without knowing which.
 */
export const PhotoCapture: React.FC<PhotoCaptureProps> = ({
    show,
    onHide,
    onCapture,
    maxSize = 1600,
    quality = 0.85,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const [error, setError] = useState<string | null>(null);
    const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
    const [preview, setPreview] = useState<FileData | null>(null);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        videoRef.current = null;
    }, []);

    const startCamera = useCallback(async (facing: 'environment' | 'user') => {
        setError(null);

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            setHasMultipleCameras(devices.filter(d => d.kind === 'videoinput').length > 1);
        } catch {
            setHasMultipleCameras(false);
        }

        // Give the modal a moment to render its body before attaching the video.
        await new Promise(resolve => setTimeout(resolve, 100));
        if (!containerRef.current) return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {facingMode: facing, width: {ideal: 1920}, height: {ideal: 1080}},
                audio: false,
            });
            streamRef.current = stream;

            const video = document.createElement('video');
            video.srcObject = stream;
            video.setAttribute('playsinline', 'true');
            video.muted = true;
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(video);
            videoRef.current = video;

            await video.play();
        } catch (e: any) {
            const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
            setError(denied
                ? 'Prístup ku kamere bol zamietnutý. Povoľte ho v nastaveniach prehliadača.'
                : 'Kameru sa nepodarilo spustiť. Skúste nahrať súbor klasicky.');
        }
    }, []);

    useEffect(() => {
        if (show) {
            setPreview(null);
            startCamera(facingMode);
        } else {
            stopCamera();
        }
        return () => stopCamera();
    }, [show, facingMode, startCamera, stopCamera]);

    const takePhoto = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;

        const scale = Math.min(1, maxSize / Math.max(video.videoWidth, video.videoHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);

        const context = canvas.getContext('2d');
        if (!context) return;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        const base64 = dataUrl.split(',')[1] ?? '';
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

        // The API rejects a size that does not match the decoded payload to the
        // byte, so this is computed exactly rather than approximated.
        const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;

        setPreview({
            filename: `foto-${stamp}.jpg`,
            mimeType: 'image/jpeg',
            size: (base64.length * 3) / 4 - padding,
            data: base64,
        });
        stopCamera();
    };

    const retake = () => {
        setPreview(null);
        startCamera(facingMode);
    };

    const confirm = () => {
        if (!preview) return;
        onCapture(preview);
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" fullscreen="lg-down">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <IconCamera size={20} /> Odfotiť
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger" className="mb-2">{error}</Alert>}

                <div className="photo-capture-view">
                    {preview ? (
                        <img
                            src={`data:${preview.mimeType};base64,${preview.data}`}
                            alt={preview.filename}
                            className="photo-capture-still"
                        />
                    ) : (
                        <div ref={containerRef} />
                    )}
                    {/* Sized like the barcode scanner: on a phone the picture is
                        bound to the screen instead of pushing the footer - and the
                        shutter with it - past the bottom edge. */}
                    <style>{`
                        .photo-capture-view video,
                        .photo-capture-view .photo-capture-still {
                            width: 100%;
                            height: auto;
                            display: block;
                            border-radius: 8px;
                        }
                        @media (max-width: 991.98px) {
                            .photo-capture-view video {
                                height: calc(100vh - 200px);
                                object-fit: cover;
                                border-radius: 0;
                            }
                            .photo-capture-view .photo-capture-still {
                                height: calc(100vh - 200px);
                                object-fit: contain;
                                border-radius: 0;
                                background: #000;
                            }
                        }
                    `}</style>
                </div>

                {!error && (
                    <div className="text-center text-muted mt-2">
                        {preview ? 'Vyhovuje snímka?' : 'Namierte kameru a odfoťte'}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <div>
                    {!preview && hasMultipleCameras && (
                        <Button
                            variant="outline-secondary"
                            onClick={() => setFacingMode(m => (m === 'environment' ? 'user' : 'environment'))}
                        >
                            <IconSwitchHorizontal size={18} /> Otočiť kameru
                        </Button>
                    )}
                    {preview && (
                        <Button variant="outline-secondary" onClick={retake}>
                            <IconRefresh size={18} /> Odfotiť znova
                        </Button>
                    )}
                </div>
                <div className="d-flex gap-2">
                    <Button variant="secondary" onClick={onHide}>Zrušiť</Button>
                    {preview
                        ? <Button variant="primary" onClick={confirm}>Použiť fotku</Button>
                        : <Button variant="primary" onClick={takePhoto} disabled={!!error}>
                            <IconCamera size={18} /> Odfotiť
                        </Button>}
                </div>
            </Modal.Footer>
        </Modal>
    );
};
